import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { uploadJsonFile } from '../../apis'; // Adjust path as needed
// Styles are imported globally via styles/index.css

function MysteryClientUploadData() {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [jsonData, setJsonData] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.json')) {
      setUploadStatus('error');
      setErrorMessage('Il file deve essere un file JSON valido');
      return;
    }

    setUploadedFile(file);
    setUploadStatus('uploading');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = JSON.parse(e.target.result);
          setJsonData(data);
          const response = await uploadJsonFile(file);
          setUploadStatus('success');
          setSuccessMessage(response.message || 'File caricato con successo!');
        } catch (parseError) {
          setUploadStatus('error');
          setErrorMessage('Errore nel parsing del JSON: ' + parseError.message);
          console.error('JSON parsing error:', parseError);
        }
      };

      reader.onerror = () => {
        setUploadStatus('error');
        setErrorMessage('Errore nella lettura del file');
      };

      reader.readAsText(file);

    } catch (uploadError) {
      setUploadStatus('error');
      setErrorMessage(
        uploadError.response?.data?.error ||
        'Errore durante il caricamento del file'
      );
      console.error('Upload error:', uploadError);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setUploadStatus('idle');
    setJsonData(null);
    setErrorMessage('');
    setSuccessMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusIcon = () => {
    switch (uploadStatus) {
      case 'uploading':
        return <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>;
      case 'success':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'error':
        return <AlertCircle className="h-6 w-6 text-red-600" />;
      default:
        return <Upload className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStatusMessage = () => {
    switch (uploadStatus) {
      case 'uploading':
        return 'Elaborazione e caricamento del file in corso...';
      case 'success':
        return successMessage || 'File caricato con successo! I dati sono ora disponibili nel modulo Mystery Client.';
      case 'error':
        return errorMessage || 'Errore durante il caricamento. Assicurati che il file sia un JSON valido.';
      default:
        return 'Trascina qui il tuo file JSON o clicca per selezionarlo';
    }
  };

  return (
    <div className="mystery-client-upload"> {/* Replaced Tailwind with custom class */}
      <div className="container"> {/* Added custom class */}
        <div className="header"> {/* Added custom class */}
          <h1 className="title"> {/* Added custom class */}
            <FileText className="icon" /> {/* Added custom class */}
            Caricamento Dati Mystery Client
          </h1>
          <p className="subtitle"> {/* Added custom class */}
            Carica un file JSON contenente i dati dei clienti misteriosi per il sistema
          </p>
        </div>

        <div className="upload-section"> {/* Added custom class */}
          <div className="info-card"> {/* Added custom class */}
            <Info className="info-icon" /> {/* Added custom class */}
            <div className="info-content"> {/* Added custom class */}
              <h3>
                Informazioni Importanti
              </h3>
              <p>
                I dati JSON caricati qui saranno automaticamente integrati nel modulo
                <strong> "Mystery Client"</strong> e saranno immediatamente disponibili
                per tutti gli utenti del sistema.
              </p>
            </div>
          </div>

          <div
            className={`upload-area ${dragOver ? 'drag-over' : ''} ${uploadStatus}`} // Use custom classes
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileInput}
              className="file-input" // Use custom class
            />

            <div className="upload-content"> {/* Added custom class */}
              {getStatusIcon()}
              <p className="upload-message"> {/* Added custom class */}
                {getStatusMessage()}
              </p>

              {uploadedFile && (
                <div className="file-info"> {/* Added custom class */}
                  <p className="file-name"> {/* Added custom class */}
                    {uploadedFile.name}
                  </p>
                  <p className="file-size"> {/* Added custom class */}
                    {(uploadedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
              )}

              {uploadStatus === 'idle' && (
                <button className="upload-button"> {/* Added custom class */}
                  Seleziona File JSON
                </button>
              )}
            </div>
          </div>

          {uploadStatus === 'success' && jsonData && (
            <div className="success-section"> {/* Added custom class */}
              <div className="data-preview"> {/* Added custom class */}
                <h3>
                  Anteprima Dati Caricati
                </h3>
                <div className="json-preview"> {/* Added custom class */}
                  <pre>
                    {JSON.stringify(jsonData, null, 2).substring(0, 500)}...
                  </pre>
                </div>
                <div className="data-stats"> {/* Added custom class */}
                  <span className="stat"> {/* Added custom class */}
                    Elementi: {Array.isArray(jsonData) ? jsonData.length : Object.keys(jsonData).length}
                  </span>
                  <span className="stat"> {/* Added custom class */}
                    Dimensione: {(uploadedFile.size / 1024).toFixed(2)} KB
                  </span>
                </div>
              </div>

              <div className="text-center"> {/* Keep Tailwind here if preferred for centering */}
                <button
                  onClick={resetUpload}
                  className="reset-button" // Added custom class
                >
                  Carica Nuovo File
                </button>
              </div>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="text-center"> {/* Keep Tailwind here if preferred for centering */}
              <button
                onClick={resetUpload}
                className="retry-button" // Added custom class
              >
                Riprova
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MysteryClientUploadData;